import { mergeClasses } from '@fluentui/react-components';
import { SetupAIHubSharedExecutionContextProvider } from '@office-start/ai-hub-execution-context-provider-lazy';
import { AppbarConnectorService } from '@office-start/appbar-connector-service';
import { useBCAIS, useCopilotEligible, usePinningEligible, useStandaloneCopilotApp, } from '@office-start/copilot-route-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { DataSecurityLevel, } from '@office-start/instrumentation-types';
import { CopilotLazyHooks, DebugInfoButton, Header, TaskModuleDialog, TeachingUiContainer, useAppStyles, useShellShyHeaderMode, useVisualRefreshStyles, } from '@office-start/office-home-app-shared';
import { mark as markPerformance } from '@office-start/performance-service';
import { logFeature } from '@office-start/telemetry-service';
import { HarmonyThemingComponent } from '@office-start/theming-service';
import { useUserEntitlements } from '@office-start/user-entitlements-service';
import React, { useEffect, useRef } from 'react';
import { useHeaderStripDisabledForBrowse } from '../../hooks/useHeaderStripDisabledForBrowse';
import { usePreWarmCopilotApp } from '../../hooks/usePreWarmCopilotApp';
import { OfficeHomeAppRouteComponent } from '../../routing/OfficeHomeAppRouteComponent';
import { PreWarmSourceTypes } from '../../utils';
import { AppBar } from '../AppBar/AppBar';
import { BackgroundImage } from '../BackgroundImage/BackgroundImage';
import { Banner } from '../Banner/Banner';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { DeleteDialog } from '../DeleteDialog/DeleteDialog';
import DragAndDropUpload from '../DragAndDropUpload/DragAndDropUpload';
import { ErrorDialog } from '../ErrorDialog/ErrorDialog';
import { FirstRun } from '../FirstRun/FirstRun';
import { LazyHooks } from '../LazyHooks/LazyHooks';
import MobileBottomNavBar from '../MobileWebPage/MobileBottomNavBar';
import { ScanDialog } from '../ScanDialog/ScanDialog';
import { ShareDialog } from '../ShareDialog/ShareDialog';
import { SupportCentral } from '../SupportCentral/SupportCentral';
import { ToastContainer } from '../ToastContainer/ToastContainer';
import { UniversalDialog } from '../UniversalDialog/UniversalDialog';
import { Upload } from '../Upload/Upload';
import { WebVitalsButton } from '../WebVitalsButton/WebVitalsButton';
export var appbarConnectorService = AppbarConnectorService.getInstance();
var AppComponent = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var officeHomeAppRouteConfig = _a.officeHomeAppRouteConfig, rootPath = _a.rootPath, scrollContentRef = _a.scrollContentRef, isMobile = _a.isMobile;
    var firstRenderRef = useRef(true);
    var styles = useAppStyles();
    var featureGates = (_c = (_b = useFeatureGates()) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.result;
    var isVisualRefreshEnabled = (_e = (_d = featureGates === null || featureGates === void 0 ? void 0 : featureGates.visualRefreshGates) === null || _d === void 0 ? void 0 : _d.visualRefreshEnabled) !== null && _e !== void 0 ? _e : false;
    var isBrowseV2Enabled = (_g = (_f = featureGates === null || featureGates === void 0 ? void 0 : featureGates.browseGates) === null || _f === void 0 ? void 0 : _f.browseV2Enabled) !== null && _g !== void 0 ? _g : false;
    var isHeaderStripDisabledForBrowse = useHeaderStripDisabledForBrowse(isBrowseV2Enabled);
    var shyHeaderMode = useShellShyHeaderMode();
    var visualRefreshStyles = useVisualRefreshStyles();
    var isDirectLandingOnMcm = appbarConnectorService.getIsCopilotChatExperienceWithDirectMcm();
    usePreWarmCopilotApp(isDirectLandingOnMcm
        ? PreWarmSourceTypes.DirectMCM
        : PreWarmSourceTypes.Home);
    var isStandaloneCopilotApp = useStandaloneCopilotApp();
    var isUsingVisualRefresh = isVisualRefreshEnabled && !isStandaloneCopilotApp;
    var userEntitlements = useUserEntitlements().data;
    var isBCAIS = useBCAIS();
    var isPinningEligible = usePinningEligible();
    var isEligibleForAnyCopilot = useCopilotEligible();
    // Log the event indicating whether copilot is pinned or unpinned for the funnel
    useEffect(function () {
        if (userEntitlements && userEntitlements.result) {
            logFeature({
                featureName: "CopilotFunnel" /* KnownFeatureNames.CopilotFunnel */,
                eventName: 'OnOfficeHomeAppLoad',
                params: {
                    IsBCAISEnabled: (!!isBCAIS).toString(),
                    IsPinningEligible: (!!isPinningEligible).toString(),
                    EligibilityFinalStatus: (!!isEligibleForAnyCopilot).toString(),
                    isStandaloneCopilotApp: (!!isStandaloneCopilotApp).toString(),
                },
            });
        }
    }, [
        isBCAIS,
        isPinningEligible,
        isEligibleForAnyCopilot,
        userEntitlements,
        isStandaloneCopilotApp,
    ]);
    if (firstRenderRef.current) {
        firstRenderRef.current = false;
        markPerformance("App" /* KnownPerformanceMarks.App */, "React_Render_Ohome_Start" /* KnownPerformanceMarks.ReactRenderOhomeStart */, DataSecurityLevel.Security, {
            AppUrl: window.location.pathname,
        });
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(HarmonyThemingComponent, { scrollContentRef: scrollContentRef }),
        React.createElement("div", { className: mergeClasses(styles.container, isUsingVisualRefresh && visualRefreshStyles.appContainer) },
            isUsingVisualRefresh && !isMobile && React.createElement(AppBar, null),
            React.createElement("div", { className: mergeClasses(styles.container, isUsingVisualRefresh &&
                    visualRefreshStyles.contentContainer) },
                React.createElement(Header, null),
                React.createElement("div", { className: mergeClasses(!isHeaderStripDisabledForBrowse && styles.content, isUsingVisualRefresh && visualRefreshStyles.content, shyHeaderMode && styles.contentShellShyHeaderMode), id: "officeHome", role: "application" },
                    isStandaloneCopilotApp ? (React.createElement(React.Fragment, null,
                        React.createElement(OfficeHomeAppRouteComponent, { officeHomeAppRouteConfig: officeHomeAppRouteConfig, rootPath: rootPath }))) : (React.createElement(React.Fragment, null,
                        !isUsingVisualRefresh && !isMobile && (React.createElement(AppBar, null)),
                        React.createElement(DragAndDropUpload, { scrollContentRef: scrollContentRef },
                            React.createElement(BackgroundImage, null),
                            React.createElement(Banner, null),
                            React.createElement(SetupAIHubSharedExecutionContextProvider, null,
                                React.createElement(OfficeHomeAppRouteComponent, { officeHomeAppRouteConfig: officeHomeAppRouteConfig, rootPath: rootPath })),
                            React.createElement(SupportCentral, null),
                            React.createElement(ErrorDialog, null),
                            React.createElement(ConfirmationDialog, null),
                            React.createElement(ShareDialog, null),
                            React.createElement(DeleteDialog, null),
                            React.createElement(UniversalDialog, null),
                            React.createElement(TeachingUiContainer, { scrollContentRef: scrollContentRef }),
                            React.createElement(ScanDialog, null)),
                        React.createElement(ToastContainer, null),
                        React.createElement(Upload, null))),
                    React.createElement(TaskModuleDialog, null)),
                React.createElement(FirstRun, null),
                isMobile && React.createElement(MobileBottomNavBar, null),
                isStandaloneCopilotApp ? (React.createElement(CopilotLazyHooks, null)) : (React.createElement(React.Fragment, null,
                    React.createElement(LazyHooks, null),
                    React.createElement(WebVitalsButton, null))),
                React.createElement(DebugInfoButton, null)))));
};
export var App = React.memo(AppComponent);
//# sourceMappingURL=App.js.map